import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Select, Space, Spin, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Box } from '@mui/material';
import axios from 'axios';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import SearchModal from '../../SearchModal';
import { Crypter } from '../../../Functions/Security/Crypter';
import Data from '../../../Data/Data.json';
import FormatSMS from '../../../Models/FormatSMS';
import Utilisateur from '../../../Models/Utilisateur';
import '../../../Styles/StyleHome.css';
import SmsPlaceholderLegend from '../../SmsPlaceholderLegend';

const codePattern = /(#([^#]+)#)/g;

const HighlightedTextArea = ({ value = '', onChange, disabled }: { value?: string; onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void; disabled?: boolean }) => {
  const [text, setText] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const escapeHtml = (input: string) =>
    input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const getHighlightedHtml = (input: string) => {
    const escaped = escapeHtml(input).replace(/\n$/g, '\n\u00a0').replace(/\n/g, '<br/>');
    return escaped.replace(codePattern, '<span style="font-weight:bold;color:#000;background:rgba(255, 235, 59, 0.6);border-radius:4px;">$1</span>');
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = e.target.value;
    setText(nextValue);
    onChange?.(e);
  };

  const syncScroll = () => {
    if (textareaRef.current && overlayRef.current) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  React.useEffect(() => {
    setText(value || '');
  }, [value]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        ref={overlayRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          padding: '11px 11px',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          overflow: 'hidden',
          pointerEvents: 'none',
          color: '#000',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit',
          backgroundColor: 'transparent',
        }}
        dangerouslySetInnerHTML={{ __html: getHighlightedHtml(text) }}
      />
      <textarea
        ref={textareaRef}
        value={text}
        onChange={handleChange}
        onScroll={syncScroll}
        disabled={disabled}
        style={{
          position: 'relative',
          width: '100%',
          minHeight: 150,
          resize: 'vertical',
          border: '1px solid #d9d9d9',
          borderRadius: 2,
          background: 'transparent',
          color: 'transparent',
          caretColor: '#000',
          outline: 'none',
          padding: 12,
          fontFamily: 'inherit',
          fontSize: '14px',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
};

const searchFormatSms = { CodeAgence: '', Table: 'FormatSms', Colonne1: 'codSmS', Colonne2: 'descriptionSMS', Colagence: '101', Choix: 22 };
const searchCodeOpe = { CodeAgence: '', Table: 'OPERAT', Colonne1: 'CODOPE', Colonne2: 'LIBELL', Colagence: '101', Choix: 22 };
const typeSmsOptions = [{ label: 'SMS', value: 'SMS' }];

type SMSRow = FormatSMS & { key: number };

const FormatSMSManage = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const authHeader = useAuthHeader();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<SMSRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<SMSRow | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchData('');
  }, []);

  const fetchData = async (searchText: string) => {
    setLoading(true);
    try {
      const encrypted = Crypter.encrypt(JSON.stringify(searchText || ''));
      const response = await axios.request({
        method: 'get',
        url: Data.ApiUrl.GetFormatSMS,
        headers: {
          Authorization: authHeader(),
          CypherSearch: encrypted,
          'Content-Type': 'application/json',
        },
      });

      if (response.data) {
        // alert(JSON.stringify(response.data));
        const result = JSON.parse(Crypter.decrypt(response.data)).Value;

        if (result.StatutCode === 200) {
          // alert(JSON.stringify(result.Result?.Table))
          const rows = (result.Result?.Table || []).map((item: SMSRow, index: number) => ({ ...item, key: index }));
          setDataSource(rows);
        } else {
          SweetAlert.fire({ title: 'Avertissement', text: result.Message, icon: 'warning' });
        }
      }
    } catch (error: any) {
      SweetAlert.fire({ title: 'Erreur', text: error?.message || 'Impossible de charger les formats SMS', icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadRecord = (record: SMSRow) => {
    setSelectedRecord(record);
    setSelectedRowKeys([record.key]);
    form.setFieldsValue({
      CodSMS: record.codSmS,
      DescriptionSMS: record.descriptionSMS,
      TypSMS: record.typSMS || 'SMS',
      Message: record.message,
      codOpe: record.codOpe,
      IsActif: record.isActif ?? false,
      lib_CodSMS: record.descriptionSMS,
      lib_codOpe: record.codOpe || '',
    });
    setIsAdding(false);
    setIsEditing(false);
  };

  const handleConsult = () => {
    const searchText = form.getFieldValue('CodSmS') || '';
    if (!searchText) {
      fetchData('');
      return;
    }
    fetchData(searchText);
  };

  const handleAdd = () => {
    form.resetFields();
    form.setFieldValue('TypSMS', 'SMS');
    form.setFieldValue('IsActif', true);
    setSelectedRecord(null);
    setSelectedRowKeys([]);
    setIsAdding(true);
    setIsEditing(false);
  };

  const handleModify = () => {
    if (!selectedRecord) {
      SweetAlert.fire({ title: 'Avertissement', text: 'Sélectionnez un format SMS avant de modifier.', icon: 'warning' });
      return;
    }
    setIsEditing(true);
    setIsAdding(false);
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedRecord(null);
    setSelectedRowKeys([]);
    setIsAdding(false);
    setIsEditing(false);
  };

  const handleSave = async (values: any) => {
    if (!isAdding && !isEditing) {
      return;
    }

    const confirm = await SweetAlert.fire({
      title: 'Confirmation',
      text: isAdding ? "Confirmer l'ajout de ce format SMS ?" : "Confirmer la modification de ce format SMS ?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui',
      cancelButtonText: 'Non',
    });

    if (!confirm.isConfirmed) return;

    const payload: FormatSMS = {
      ...selectedRecord,
      ...values,
      UtiSai: user.coduti,
      UtiModif: user.coduti,
      // CodOpe: selectedRecord?.codOpe || '',
      DatCre: selectedRecord?.datCre || new Date().toISOString(),
      DatModif: new Date().toISOString(),
      UtiVal: selectedRecord?.utiVal || '',
      DatVal: selectedRecord?.datVal || '',
    };

    const url = isAdding ? Data.ApiUrl.CreateFormatSMS : Data.ApiUrl.UpdateFormatSMS;
    await submitPayload(url, payload, isAdding ? "post" : "put");
  };

  const submitPayload = async (url: string, payload: any, methode: string) => {
    setLoading(true);
    try {
      const encrypted = Crypter.encrypt(JSON.stringify(payload));
      const response = await axios.request({
        method: methode,
        url,
        headers: {
          Authorization: authHeader(),
          'Content-Type': 'application/json',
        },
        data: encrypted,
      });

      if (response.data) {
        const result = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (result.StatutCode === 200) {
          SweetAlert.fire({ title: result.Message, icon: 'success' });
          handleCancel();
          fetchData('');
        } else {
          SweetAlert.fire({ title: 'Avertissement', text: result.Message, icon: 'warning' });
        }
      }
    } catch (error: any) {
      SweetAlert.fire({ title: 'Erreur', text: error?.message || 'Erreur serveur', icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) {
      SweetAlert.fire({ title: 'Avertissement', text: 'Sélectionnez un format SMS avant de supprimer.', icon: 'warning' });
      return;
    }

    const result = await SweetAlert.fire({ title: 'Supprimer ce format SMS ?', showCancelButton: true, confirmButtonText: 'Oui', icon: 'warning' });
    if (!result.isConfirmed) {
      return;
    }

    await submitPayload(Data.ApiUrl.DeleteFormatSMS, { ...selectedRecord }, "delete");
  };

  const handleValidate = async () => {
    if (!selectedRecord) {
      SweetAlert.fire({ title: 'Avertissement', text: 'Sélectionnez un format SMS avant de valider.', icon: 'warning' });
      return;
    }
    await submitPayload(Data.ApiUrl.ValidateFormatSMS, { ...selectedRecord }, "put");
  };

  const handleToggleActive = async () => {
    if (!selectedRecord) {
      SweetAlert.fire({ title: 'Avertissement', text: 'Sélectionnez un format SMS avant de changer son statut.', icon: 'warning' });
      return;
    }
    const url = selectedRecord.isActif ? Data.ApiUrl.DeactivateFormatSMS : Data.ApiUrl.ActivateFormatSMS;
    await submitPayload(url, { ...selectedRecord }, "put");
  };

  const columns: ColumnsType<SMSRow> = [
    { title: 'Code', dataIndex: 'codSmS', width: 140 },
    { title: 'Description', dataIndex: 'descriptionSMS' },
    // { title: 'Message', dataIndex: 'message', render: (value) => <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span> },
    { title: 'Type', dataIndex: 'typSMS', },
    {
      title: 'Code Opération',
      dataIndex: 'codOpe',
      width: 180,
      render: (value) => value || '-',
    },

    { title: 'Date de création', dataIndex: 'datCre' },
    { title: 'Crée par', dataIndex: 'utiSai' },
    {
      title: 'Date de modification',
      dataIndex: 'datModif',
      render: (value) => value || '-',
    },
    {
      title: 'Crée par',
      dataIndex: 'utiSai',
      render: (value) => value || '-',
    },
    {
      title: 'Date de validation',
      dataIndex: 'datVal',
      render: (value) => value || '-',
    },

    {
      title: 'Validé par',
      dataIndex: 'utiVal',
      render: (value) => value || '-',
    },
    {
      title: 'Statut',
      dataIndex: 'isActif',
      width: 110,
      render: (value: boolean) => (value ? <Tag color="#116599">Actif</Tag> : <Tag color="red">Inactif</Tag>),
    },
  ];

  const rowSelection = {
    type: 'radio' as const,
    selectedRowKeys,
    onChange: (keys: React.Key[], rows: SMSRow[]) => {
      const record = rows[0];
      if (record) {
        loadRecord(record);
      }
    },
  };

  return (
    <div className='tab'>
      <div className='formTitle' style={{ textAlign: 'center', marginBottom: 16 }}>
        <b>Gestion des formats SMS</b>
      </div>

      <Spin spinning={loading} size='large'>
        <Form form={form} layout='vertical' onFinish={handleSave} autoComplete='off'>
          <div className='bG'>
            <legend><span style={{ color: '#116599' }}>Actions</span></legend>
            <Space direction='vertical' style={{ width: '100%' }}>
              <Button size='large' className='bt_add' onClick={handleConsult} block>
                Consulter
              </Button>
              <Button size='large' className='bt_add' onClick={handleAdd} block>
                Ajouter
              </Button>
              <Button size='large' className='bt_add' onClick={handleModify} disabled={!selectedRecord} block>
                Modifier
              </Button>
              <Button size='large' className='bt_add' htmlType='submit' disabled={!isAdding && !isEditing} block>
                Enregistrer
              </Button>
              <Button size='large' className='bt_add' onClick={handleDelete} disabled={!selectedRecord} block>
                Supprimer
              </Button>
              <Button size='large' className='bt_add' onClick={handleValidate} disabled={!selectedRecord || !!selectedRecord?.utiVal} block>
                Valider
              </Button>
              <Button size='large' className='bt_add' onClick={handleToggleActive} disabled={!selectedRecord} block>
                {selectedRecord?.isActif ? 'Désactiver' : 'Activer'}
              </Button>
              <Button size='large' className='bt_add' onClick={() => fetchData('')} block>
                Actualiser
              </Button>
              <Button size='large' className='bt_add' onClick={handleCancel} block>
                Annuler
              </Button>
            </Space>
          </div>

          <div className='clientP'>
            <div className='border' style={{ padding: 16, marginBottom: 16, width: "86%", height: "100%" }}>
              <div style={{ marginBottom: 16, display: 'block', fontWeight: 600 }}>
                Détails du format SMS
              </div>

              <Box display='flex' flexWrap='wrap' gap={16}>
                <Box flex={1}>
                  {isAdding ? (
                    <Form.Item name='CodSmS' label='Code Format SMS' rules={[{ required: true, message: 'Le code est obligatoire' }]}>
                      <Input placeholder='Code Format SMS' />
                    </Form.Item>
                  ) : (
                    <SearchModal
                      // width={10}
                      Titre='Recherche Format SMS'
                      label='Code Format SMS'
                      name='CodSMS'
                      Parametre={searchFormatSms}
                      form={form}
                    />
                  )}
                </Box>
                <Box minWidth={280} flex={1}>
                  <Form.Item name='codOpe' style={{ marginBottom: 0 }}>
                    <SearchModal
                      // width={10}
                      notRequired
                      Titre='Recherche Code Opération'
                      label='Code Opération'
                      name='codOpe'
                      Parametre={searchCodeOpe}
                      form={form}
                    />
                  </Form.Item>
                </Box>
              </Box>
              <br />
              <Box display='flex' flexWrap='wrap' gap={16} mt={2}>
                <Box minWidth={280} flex={1}>
                  <Form.Item name='DescriptionSMS' label='Description' rules={[{ required: true, message: 'La description est obligatoire' }]}>
                    <Input disabled={!isAdding && !isEditing} placeholder='Description du format SMS' />
                  </Form.Item>
                </Box>
                <Box minWidth={220} flex={1}>
                  <Form.Item name='TypSMS' label='Type' initialValue='SMS'>
                    <Select options={typeSmsOptions} disabled />
                  </Form.Item>
                </Box>
                <Box minWidth={180} flex={1}>
                  <Form.Item name='IsActif' label='Actif' valuePropName='checked' initialValue={true}>
                    <Select disabled={!isAdding && !isEditing} options={[{ label: 'Oui', value: true }, { label: 'Non', value: false }]} />
                  </Form.Item>
                </Box>
              </Box>

              <Box display='flex' flexWrap='wrap' gap={5} mt={2}>
                <Box minWidth={"60%"} flex={1}>
                  <Form.Item name='Message' label='Message' rules={[{ required: true, message: 'Le message est obligatoire' }]}>
                    <HighlightedTextArea disabled={!isAdding && !isEditing} />
                  </Form.Item>
                </Box>
                <Box padding={1} flex={1} style={{ border: '1px solid lightblue', borderRadius: 4, backgroundColor: 'white', overflowY: 'auto', maxHeight: 150, marginTop: 24 }}>
                  {/* legendes des code présent dans le message */}
                  <div style={{ fontSize: 12, color: 'gray' }}>
                    <SmsPlaceholderLegend onInsert={()=>{}} />
                  </div>
                </Box>
              </Box>
            </div>
            <br /><br />
            <div className='border-left tabElement2' style={{ padding: 0 }}>
              <Spin spinning={loading}>
                <Table<SMSRow>
                  size='small'
                  loading={loading}
                  rowSelection={rowSelection}
                  columns={columns}
                  dataSource={dataSource}
                  pagination={{ defaultPageSize: 5, position: ['bottomRight'] }}
                  scroll={{ x: true }}
                />
              </Spin>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default FormatSMSManage;
